import type React from 'react';
import { ReactComponent as TransparencyHigh } from '@/assets/icons/tools/transparencyHigh.svg';
import { ReactComponent as TransparencyMedium } from '@/assets/icons/tools/transparencyMedium.svg';
import { ReactComponent as TransparencyLow } from '@/assets/icons/tools/transparencyLow.svg';
import { ReactComponent as TransparencyOpaque } from '@/assets/icons/tools/transparencyOpaque.svg';
import { IconButton, Tooltip } from '@mui/material';
import { Transparency } from '@/engine/app/interfaces';
import type { WithMixed } from '../types';
import { useTheme } from '@mui/material/styles';
import { getMixedColor } from './getMixedColor';

export const TransparencyIconButton: React.FC<{
  transparency: WithMixed<Transparency>;
  onChange: (transparency: Transparency) => void;
}> = ({ transparency, onChange }) => {
  const theme = useTheme();

  const handleChangeDecrease = (): void => {
    switch (transparency) {
      case Transparency.Opaque:
      case 'mixed':
        onChange(Transparency.High);
        break;
      case Transparency.High:
        onChange(Transparency.Medium);
        break;
      case Transparency.Medium:
        onChange(Transparency.Low);
        break;
      case Transparency.Low:
        onChange(Transparency.Opaque);
        break;
      default:
        throw new Error('Logic Error: Unknown value');
    }
  };

  const handleChangeIncrease = (): void => {
    switch (transparency) {
      case Transparency.Low:
      case 'mixed':
        onChange(Transparency.Medium);
        break;
      case Transparency.Medium:
        onChange(Transparency.High);
        break;
      case Transparency.High:
        onChange(Transparency.Opaque);
        break;
      case Transparency.Opaque:
        onChange(Transparency.Low);
        break;
      default:
        throw new Error('Logic Error: Unknown value');
    }
  };

  const { label, icon: Icon } = transparencies[transparency];

  const color = transparency === 'mixed' ? getMixedColor(theme) : theme.palette.primary.main;

  return (
    <Tooltip disableInteractive title={label} placement="top">
      <IconButton
        onClick={(): void => handleChangeDecrease()}
        onContextMenu={(e): void => {
          e.preventDefault();
          handleChangeIncrease();
        }}
      >
        <Icon width={27} style={{ fill: color }} />
      </IconButton>
    </Tooltip>
  );
};

const transparencies: Record<
  WithMixed<Transparency>,
  { label: string; icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>> }
> = {
  [Transparency.High]: {
    label: 'High',
    icon: TransparencyHigh
  },
  [Transparency.Medium]: {
    label: 'Medium',
    icon: TransparencyMedium
  },
  [Transparency.Low]: {
    label: 'Low',
    icon: TransparencyLow
  },
  [Transparency.Opaque]: {
    label: 'Opaque',
    icon: TransparencyOpaque
  },
  mixed: {
    label: 'Mixed',
    icon: TransparencyMedium
  }
};
